import { Decimal, toDecimal, type DecimalInput } from './decimal.js';
import type { Currency } from '../types/enums.js';

/** An amount that always carries its currency. Immutable. */
export interface Money {
  readonly amount: Decimal;
  readonly currency: Currency;
}

/** Minor units per currency, used for display rounding only — never for storage. */
const DISPLAY_DECIMALS: Record<Currency, number> = {
  HUF: 0,
  EUR: 2,
  USD: 2,
  THB: 2,
};

export function money(amount: DecimalInput, currency: Currency): Money {
  return { amount: toDecimal(amount), currency };
}

export function zero(currency: Currency): Money {
  return { amount: new Decimal(0), currency };
}

export class CurrencyMismatchError extends Error {
  constructor(left: Currency, right: Currency) {
    super(`Cannot combine ${left} and ${right} without an FX conversion`);
    this.name = 'CurrencyMismatchError';
  }
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount.plus(b.amount), currency: a.currency };
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount.minus(b.amount), currency: a.currency };
}

export function multiply(a: Money, factor: DecimalInput): Money {
  return { amount: a.amount.times(toDecimal(factor)), currency: a.currency };
}

export function divide(a: Money, divisor: DecimalInput): Money {
  return { amount: a.amount.dividedBy(toDecimal(divisor)), currency: a.currency };
}

export function negate(a: Money): Money {
  return { amount: a.amount.negated(), currency: a.currency };
}

export function sum(values: readonly Money[], currency: Currency): Money {
  return values.reduce<Money>((acc, value) => add(acc, value), zero(currency));
}

export function isZero(a: Money): boolean {
  return a.amount.isZero();
}

export function compare(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return a.amount.comparedTo(b.amount);
}

/** Round to the currency's display precision. Storage keeps full precision. */
export function roundForDisplay(a: Money): Money {
  return {
    amount: a.amount.toDecimalPlaces(DISPLAY_DECIMALS[a.currency], Decimal.ROUND_HALF_UP),
    currency: a.currency,
  };
}

/** Lossless string form for the database and the wire. */
export function toDecimalString(a: Money): string {
  return a.amount.toFixed();
}

export interface FormatMoneyOptions {
  locale?: string;
  /** Show the currency code/symbol. Defaults to true. */
  withCurrency?: boolean;
  /** Override the currency's default display decimals. */
  decimals?: number;
}

export function formatMoney(a: Money, options: FormatMoneyOptions = {}): string {
  const { locale = 'hu-HU', withCurrency = true, decimals } = options;
  const fractionDigits = decimals ?? DISPLAY_DECIMALS[a.currency];
  const formatter = new Intl.NumberFormat(locale, {
    ...(withCurrency ? { style: 'currency' as const, currency: a.currency } : {}),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  // Decimal -> number only at the very last step, for formatting.
  return formatter.format(a.amount.toDecimalPlaces(fractionDigits).toNumber());
}

export function displayDecimals(currency: Currency): number {
  return DISPLAY_DECIMALS[currency];
}
